/* eslint-disable i18next/no-literal-string -- i18n deferred to v2 per plan */
import { Card, Badge } from '@agion/ui'
import { AlertCircle } from 'lucide-react'
import type { InfraOperation } from '@/core/infra_types'

interface Props {
  operation: InfraOperation
}

const STAGE_KEYS = ['precheck', 'apt', 'postinst', 'verify'] as const

const FLYWAY_MODE_LABELS: Record<string, string> = {
  standard: 'Standard (migrate only)',
  repair_then_migrate: 'Repair then migrate',
  validate_only: 'Validate only',
  skip: 'Skip Flyway',
}

export function HostUpgradeOverview({ operation }: Props) {
  const params = (operation.parameters ?? {}) as Record<string, unknown>
  const flywayMode = String(params['flywayMode'] ?? params['flyway_mode'] ?? 'repair_then_migrate')
  const dryRun = Boolean(params['dryRun'] ?? params['dry_run'] ?? false)
  const skipStagesRaw = (params['skipStages'] ?? params['skip_stages'] ?? []) as unknown
  const skipStages = Array.isArray(skipStagesRaw) ? skipStagesRaw.map(String) : []
  const skipReasonsRaw = (params['skipReasons'] ?? params['skip_reasons'] ?? {}) as Record<string, unknown>
  const skipReasons = Object.fromEntries(
    Object.entries(skipReasonsRaw).map(([k, v]) => [k, String(v ?? '')])
  )

  const hasParameters =
    flywayMode !== 'repair_then_migrate' || dryRun || skipStages.length > 0

  return (
    <Card>
      <div className="p-4 space-y-4">
        {operation.errorMessage && (
          <div
            className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300"
            role="alert"
          >
            <AlertCircle size={16} className="mt-0.5 flex-none" />
            <div>
              <p className="font-semibold">Operation failed</p>
              <p className="mt-1 font-mono text-xs">{operation.errorMessage}</p>
            </div>
          </div>
        )}

        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Submitted parameters
          </h3>
          {!hasParameters ? (
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Default upgrade — no overrides applied
              <span className="ml-1 text-xs text-slate-500 dark:text-slate-400">
                (Flyway: repair then migrate, no dry run, no stages skipped)
              </span>
            </p>
          ) : (
            <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <dt className="text-xs text-slate-500 dark:text-slate-400">Flyway mode</dt>
                <dd className="mt-0.5 text-sm font-medium text-slate-900 dark:text-slate-100">
                  {FLYWAY_MODE_LABELS[flywayMode] ?? flywayMode}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500 dark:text-slate-400">Dry run</dt>
                <dd className="mt-0.5">
                  {dryRun ? (
                    <Badge variant="warning">Yes — no actual changes</Badge>
                  ) : (
                    <span className="text-sm text-slate-600 dark:text-slate-300">No</span>
                  )}
                </dd>
              </div>
              {skipStages.length > 0 && (
                <div className="sm:col-span-2">
                  <dt className="text-xs text-slate-500 dark:text-slate-400">Skipped stages</dt>
                  <dd className="mt-1 flex flex-wrap gap-2">
                    {STAGE_KEYS.filter(s => skipStages.includes(s)).map(stage => (
                      <Badge key={stage} variant="secondary" className="font-mono text-xs">
                        {stage}
                        {skipReasons[stage] && (
                          <span className="ml-1 font-normal italic text-slate-600 dark:text-slate-400">
                            — {skipReasons[stage]}
                          </span>
                        )}
                      </Badge>
                    ))}
                  </dd>
                </div>
              )}
            </dl>
          )}
        </section>
      </div>
    </Card>
  )
}
